import React from "react"
import { Row, Col, Select, AutoComplete, Form } from "antd"
import moment, { Moment } from "moment-timezone"

type DOBPickerProps = {
  value: Moment | null
  onChange: (val: Moment | null) => void
  disabled?: boolean
  size?: "small" | "middle" | "large"
  getPopupContainer?: (trigger?: HTMLElement) => HTMLElement
}

const MONTHS = [
  { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
  { value: 4, label: "April" },   { value: 5, label: "May" },      { value: 6, label: "June" },
  { value: 7, label: "July" },    { value: 8, label: "August" },   { value: 9, label: "September" },
  { value: 10, label: "October" },{ value: 11, label: "November" },{ value: 12, label: "December" },
]

const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
const daysInMonth = (m?: number, y?: number) => {
  if (!m) return 31
  if (m === 2) {
    // Allow Feb 29 as a selectable option when year is not set.
    // Final validation happens only when year is provided (see commit()).
    return y === undefined ? 29 : (isLeap(y) ? 29 : 28)
  }
  if ([4, 6, 9, 11].includes(m)) return 30
  return 31
}

const NOW_M = moment()
const MAX_DATE_M = NOW_M.clone().subtract(1, "year").endOf("day")
const MIN_DATE_M = NOW_M.clone().subtract(110, "year").startOf("day")

export default function DOBPicker({
  value,
  onChange,
  disabled,
  size = "middle",
  getPopupContainer,
}: DOBPickerProps) {
  const [month, setMonth] = React.useState<number | undefined>(value ? value.month() + 1 : undefined)
  const [day, setDay] = React.useState<number | undefined>(value ? value.date() : undefined)
  const [yearStr, setYearStr] = React.useState<string>(value ? String(value.year()) : "")
  const [leapNudge, setLeapNudge] = React.useState(false)
  const [rangeError, setRangeError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!value) {
      setMonth(undefined); setDay(undefined); setYearStr(""); setLeapNudge(false); setRangeError(null); return
    }
    setMonth(value.month() + 1); setDay(value.date()); setYearStr(String(value.year())); setLeapNudge(false); setRangeError(null)
  }, [value])

  const commit = (m?: number, d?: number, yS?: string) => {
    setLeapNudge(false); setRangeError(null)
    const y = yS ? Number(yS) : undefined
    // only finalize when month, day and year are present and year is a valid number
    if (!m || !d || !y || Number.isNaN(y)) { onChange(null); return }
    const dim = daysInMonth(m, y)
    if (d > dim) { setDay(undefined); setLeapNudge(true); onChange(null); return }
    const next = moment().year(y).month(m - 1).date(d).startOf("day")
    if (!next.isValid()) { onChange(null); return }
    if (next.isAfter(MAX_DATE_M)) { setRangeError(`Birthdate must be ≤ ${MAX_DATE_M.format("YYYY-MM-DD")} (≥ 1 year old).`); onChange(null); return }
    if (next.isBefore(MIN_DATE_M)) { setRangeError(`Birthdate must be ≥ ${MIN_DATE_M.format("YYYY-MM-DD")} (≤ 110 years old).`); onChange(null); return }
    onChange(next)
  }

  const thisYear = NOW_M.year()
  const suggestedYears = React.useMemo(() => {
    const list: number[] = []
    for (let y = thisYear - 3; y >= thisYear - 22; y--) list.push(y)
    return list.map((y) => ({ value: String(y), label: String(y) }))
  }, [thisYear])

  const dayOptions = React.useMemo(() => {
    const yNum = yearStr ? Number(yearStr) : undefined
    const dim = daysInMonth(month, yNum)
    return Array.from({ length: dim }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
  }, [month, yearStr])

  const popup = getPopupContainer ?? (() => document.body)

  const ageClass =
    value === null ? "ok" : moment().diff(value, "years") > 0 ? "ok" : "age-0"

  // Helper: if Feb and day 29 selected but year not chosen, show a gentle helper
  const feb29Pending = month === 2 && day === 29 && (!yearStr || yearStr.trim() === "")

  return (
    <Row className={ageClass} gutter={8} align="middle" wrap={false} style={{ width: "100%", overflowX: "auto" }}>
      <Col xs={8} sm={8} md={8} lg={6} xl={6}>
        <Form.Item style={{ marginBottom: 0 }} required>
          <Select size={size} disabled={disabled} value={month} placeholder="Month" options={MONTHS} allowClear getPopupContainer={popup} style={{ width: "100%" }}
            onChange={(m) => { setMonth(m); if (day && m && day > daysInMonth(m, Number(yearStr))) setDay(undefined); commit(m, day, yearStr) }} />
        </Form.Item>
      </Col>

      <Col xs={8} sm={8} md={8} lg={4} xl={4}>
        <Form.Item style={{ marginBottom: 0 }} required>
          <Select size={size} disabled={disabled} value={day} placeholder="Day" options={dayOptions} allowClear getPopupContainer={popup} style={{ width: "100%" }}
            onChange={(d) => { setDay(d); commit(month, d, yearStr) }} />
        </Form.Item>
      </Col>

      <Col xs={8} sm={8} md={8} lg={4} xl={4}>
        <Form.Item
          style={{ marginBottom: 0 }}
          required
          validateStatus={rangeError ? "error" : undefined}
          help={
            rangeError
              || (leapNudge ? "Selected day is invalid for this year (e.g., Feb 29). Please pick a valid day." : undefined)
              || (feb29Pending ? "Feb 29 selected. Please select a year (leap year) to confirm Feb 29, otherwise choose another day." : undefined)
          }
        >
          <AutoComplete disabled={disabled} value={yearStr} placeholder="Year" allowClear style={{ width: "100%" }} options={suggestedYears} getPopupContainer={popup}
            onSelect={(val) => { setYearStr(val); commit(month, day, val) }}
            onChange={(text) => setYearStr(text)}
            onBlur={() => commit(month, day, yearStr)} />
        </Form.Item>
      </Col>
    </Row>
  )
}